import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  Building2,
  UserRound,
  Stethoscope,
  Power,
  SlidersHorizontal,
  Handshake,
  Percent,
  StickyNote,
  Users,
  Save,
  X,
} from 'lucide-react';
import {
  useReferralPartners,
  useCreateReferralPartner,
  useUpdateReferralPartner,
  useDeleteReferralPartner,
  useBulkTrashReferralPartners,
} from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { usePermissions } from '../../hooks/usePermissions';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { ValidatedNumberInput } from '../../components/ui/validated-number-input';
import { DoctorSearchSelect } from '../../components/ui/DoctorSearchSelect';
import { EmployeeSearchSelect } from '../../components/ui/EmployeeSearchSelect';
import { DesignationSearchSelect } from '../../components/ui/DesignationSearchSelect';
import { SectionHeader, TypeCard, ToggleSwitch } from '../../components/ui/modal-kit';
import { ReferralPartnerDetailsModal } from './modals/ReferralPartnerDetailsModal';
import type { ReferralPartner, ReferralPartnerType } from '../../services/httpServices/referralService';

const LIMIT = 20;

type ReferrerTypeFilter = 'ALL' | 'DOCTOR' | 'EMPLOYEE' | 'OTHER';
type StatusFilter = 'ALL' | 'ACTIVE' | 'INACTIVE';

const REFERRER_TYPE_OPTIONS: { value: ReferrerTypeFilter; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'DOCTOR', label: 'Doctor' },
  { value: 'EMPLOYEE', label: 'Employee' },
  { value: 'OTHER', label: 'Other' },
];

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'ALL', label: 'All Statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

interface ReferralPartnersPageProps {
  heading?: string;
  subheading?: string;
  /** Rendered inside the Referrals workspace: the page heading is rendered
   * inline with the Trash and Add Referral actions so the title and actions
   * share a single header row. */
  embedded?: boolean;
}

/**
 * Referral-partner registry. A partner is either a **Doctor** (linked to a
 * doctor) or **Other** (anyone else). Rendered as the Partners tab of the
 * Referrals workspace.
 *
 * Fully API-driven: search, type/status filters, pagination, row selection with
 * bulk move-to-trash, and a trash view with a live counter.
 */
export function ReferralPartnersPage({ heading, subheading, embedded }: ReferralPartnersPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const canDelete = hasPermission('referrals:delete');
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [page, setPage] = useState(() => {
    const p = params.get('page');
    return p ? Math.max(1, parseInt(p, 10)) : 1;
  });
  const [search, setSearch] = useState(() => params.get('search') || '');
  const debounced = useDebounce(search, 400);
  const [referrerType, setReferrerType] = useState<ReferrerTypeFilter>(
    () => (params.get('refType') as ReferrerTypeFilter) || 'ALL',
  );
  const [status, setStatus] = useState<StatusFilter>(() => (params.get('status') as StatusFilter) || 'ALL');
  const [showFilters, setShowFilters] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ReferralPartner | null>(null);
  const [viewing, setViewing] = useState<ReferralPartner | null>(null);
  const [deleting, setDeleting] = useState<ReferralPartner | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const [bulkConfirm, setBulkConfirm] = useState(false);

  // Doctor / Other → API filter.
  const effectiveType = referrerType === 'ALL' ? undefined : referrerType;
  const isActiveFilter = status === 'ACTIVE' ? true : status === 'INACTIVE' ? false : undefined;

  const { data, isLoading, isFetching } = useReferralPartners(page, LIMIT, {
    search: debounced.trim() || undefined,
    type: effectiveType,
    isActive: isActiveFilter,
  });
  const rows = useMemo(() => data?.data ?? [], [data]);
  const meta = data?.meta;
  const trashedCount = Number(data?.stats?.trashed ?? 0);

  const deleteMutation = useDeleteReferralPartner();
  const updateMutation = useUpdateReferralPartner();
  const bulkTrashMutation = useBulkTrashReferralPartners();

  // Reset to the first page whenever the filters change.
  useEffect(() => {
    setPage(1);
  }, [debounced, referrerType, status]);

  // Reflect the active filters in the URL so the view is shareable / reloadable.
  // Merges into the existing query string so a parent workspace tab (`?tab=`) is
  // preserved, and skips the navigation when nothing changed to avoid a loop.
  useEffect(() => {
    const next = new URLSearchParams(location.search);
    next.delete('page');
    next.delete('search');
    next.delete('refType');
    next.delete('status');
    if (page > 1) next.set('page', String(page));
    if (debounced) next.set('search', debounced);
    if (referrerType !== 'ALL') next.set('refType', referrerType);
    if (status !== 'ALL') next.set('status', status);
    const qs = next.toString();
    const target = `${location.pathname}${qs ? `?${qs}` : ''}`;
    const current = `${location.pathname}${location.search}`;
    if (target !== current) navigate(target, { replace: true });
  }, [page, debounced, referrerType, status, navigate, location.pathname, location.search]);

  // Drop selected ids that are no longer visible (e.g. after paging/filtering).
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(rows.map((r) => r.id));
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [rows]);

  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id));
  const someSelected = selectedIds.size > 0;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) rows.forEach((r) => next.delete(r.id));
      else rows.forEach((r) => next.add(r.id));
      return next;
    });
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (partner: ReferralPartner) => {
    setEditing(partner);
    setFormOpen(true);
  };

  const handleToggleActive = async (partner: ReferralPartner) => {
    try {
      await updateMutation.mutateAsync({ id: partner.id, data: { isActive: !partner.isActive } });
      toast.success(partner.isActive ? 'Referral partner deactivated' : 'Referral partner activated');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update referral partner');
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMutation.mutateAsync(deleting.id);
      toast.success('Referral partner moved to trash');
      setDeleting(null);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete referral partner');
    }
  };

  const handleBulkTrash = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      const res: any = await bulkTrashMutation.mutateAsync(ids);
      const trashed = res?.data?.trashed?.length ?? ids.length;
      toast.success(`Moved ${trashed} referral partner${trashed === 1 ? '' : 's'} to trash`);
      setSelectedIds(new Set());
      setBulkConfirm(false);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to move referral partners to trash');
    }
  };

  const clearFilters = () => {
    setSearch('');
    setReferrerType('ALL');
    setStatus('ALL');
    setPage(1);
  };

  const hasActiveFilters = !!search || referrerType !== 'ALL' || status !== 'ALL';
  const activeFilterCount =
    (search ? 1 : 0) +
    (referrerType !== 'ALL' ? 1 : 0) +
    (status !== 'ALL' ? 1 : 0);
  const columnCount = canDelete ? 11 : 10;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {embedded ? (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Handshake size={20} className="text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-text">{heading || 'Referrals'}</h1>
              <p className="text-sm text-text-muted mt-0.5">
                {subheading || 'Referral partners — internal employees, doctors and outside referrers.'}
              </p>
            </div>
          </div>
        ) : (
          <div>
            <h1 className="text-xl font-bold text-text">{heading || 'Referrals'}</h1>
            <p className="text-sm text-text-muted mt-0.5">
              {subheading || 'People who refer patients — internal employees, doctors and outside referrers.'}
            </p>
          </div>
        )}
        <div className="flex items-center gap-2 ml-auto">
          {canDelete && (
            <button
              type="button"
              onClick={() => navigate('/referrals/trash')}
              className="inline-flex items-center gap-2 px-4 py-2 border border-border text-text-muted hover:text-danger hover:border-danger rounded-lg transition-colors text-sm font-medium"
              title="View trashed referral partners"
            >
              <Trash2 size={16} />
              Trash
              {trashedCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[10px] font-semibold leading-none">
                  {trashedCount > 99 ? '99+' : trashedCount}
                </span>
              )}
            </button>
          )}
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
          >
            <Plus size={16} />
            Add Referral
          </button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-surface rounded-xl border border-border p-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, mobile, organization or code…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowFilters((v) => !v)}
              className={`px-3 py-2 border rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap ${
                showFilters || hasActiveFilters
                  ? 'bg-primary/10 border-primary text-primary'
                  : 'text-text-muted hover:text-text border-border'
              }`}
            >
              <SlidersHorizontal size={14} />
              Filters
              {activeFilterCount > 0 && (
                <span className="ml-0.5 w-5 h-5 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
              >
                <X size={14} />
                Clear
              </button>
            )}
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-2 border-t border-border">
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Referral Type</label>
              <select
                value={referrerType}
                onChange={(e) => setReferrerType(e.target.value as ReferrerTypeFilter)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                {REFERRER_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as StatusFilter)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Bulk actions */}
      {someSelected && canDelete && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.size}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkConfirm(true)}
              disabled={bulkTrashMutation.isPending}
              className="px-3 py-1.5 rounded-lg bg-danger text-white text-sm font-medium hover:bg-danger/90 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkTrashMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Move to trash
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkTrashMutation.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      <div className="bg-surface border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase text-text-muted">
                {canDelete && (
                  <th className="px-4 py-3 w-10">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      disabled={rows.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                )}
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Code</th>
                <th className="px-4 py-3 font-medium">Mobile</th>
                <th className="px-4 py-3 font-medium">Organization / Designation</th>
                <th className="px-4 py-3 font-medium text-right">Default %</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Created</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(isLoading || isFetching) && rows.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className="px-4 py-10 text-center text-text-muted">
                    <Loader2 size={18} className="animate-spin inline mr-2" />
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className="px-4 py-10 text-center text-text-muted">
                    {hasActiveFilters ? (
                      <span>
                        No referral partners match your filters.{' '}
                        <button onClick={clearFilters} className="text-primary hover:underline">
                          Clear filters
                        </button>
                      </span>
                    ) : (
                      'No referral partners found.'
                    )}
                  </td>
                </tr>
              ) : (
                rows.map((partner) => {
                  const isDoctor = partner.type === 'DOCTOR';
                  const isEmployee = partner.type === 'EMPLOYEE';
                  return (
                    <tr key={partner.id} className="border-b border-border last:border-0 hover:bg-background/60">
                      {canDelete && (
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(partner.id)}
                            onChange={() => toggleSelectOne(partner.id)}
                            className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                          />
                        </td>
                      )}
                      <td className="px-4 py-3 font-medium text-text">
                        <button
                          type="button"
                          onClick={() => setViewing(partner)}
                          className="text-left text-primary hover:underline focus:outline-none focus-visible:underline"
                          title="View referral partner details"
                        >
                          {partner.name}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            isDoctor
                              ? 'bg-primary/10 text-primary'
                              : isEmployee
                                ? 'bg-success/10 text-success'
                                : 'bg-warning/10 text-warning'
                          }`}
                        >
                          {isDoctor ? (
                            <Stethoscope size={12} />
                          ) : isEmployee ? (
                            <UserRound size={12} />
                          ) : (
                            <Building2 size={12} />
                          )}
                          {isDoctor ? 'Doctor' : isEmployee ? 'Employee' : 'Other'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-text-muted">{partner.code}</td>
                      <td className="px-4 py-3 text-text-muted">{partner.mobileNumber || '—'}</td>
                      <td className="px-4 py-3 text-text-muted">
                        {[partner.organization, partner.designation].filter(Boolean).join(' · ') || '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-text">
                        {Number(partner.defaultSharePercent) || 0}%
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                            partner.isActive ? 'bg-success/10 text-success' : 'bg-background text-text-muted'
                          }`}
                        >
                          {partner.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-text-muted whitespace-nowrap">
                        {partner.createdAt ? new Date(partner.createdAt).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => handleToggleActive(partner)}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                            title={partner.isActive ? 'Deactivate' : 'Activate'}
                          >
                            <Power size={15} />
                          </button>
                          <button
                            type="button"
                            onClick={() => openEdit(partner)}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                            title="Edit"
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleting(partner)}
                            className="p-1.5 rounded-lg hover:bg-danger/10 text-text-muted hover:text-danger transition-colors"
                            title="Move to trash"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {meta && (
          <Pagination
            page={meta.page}
            totalPages={meta.totalPages}
            total={meta.total}
            limit={meta.limit}
            onPageChange={setPage}
            itemName="referral partners"
          />
        )}
      </div>

      {formOpen && (
        <ReferralFormModal
          partner={editing}
          onClose={() => setFormOpen(false)}
          onSaved={() => {
            setFormOpen(false);
            toast.success(editing ? 'Referral partner updated' : 'Referral partner created');
          }}
        />
      )}

      {viewing && (
        <ReferralPartnerDetailsModal
          partner={viewing}
          onClose={() => setViewing(null)}
          onEdit={(partner) => {
            setViewing(null);
            openEdit(partner);
          }}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        title="Move referral partner to trash"
        description={
          <>
            Move <span className="font-medium text-text">{deleting?.name}</span> to the trash? It can be
            restored later.
          </>
        }
        confirmLabel="Move to trash"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleting(null)}
      />

      <ConfirmDialog
        open={bulkConfirm}
        title="Move referral partners to trash"
        description={`Move ${selectedIds.size} selected referral partner(s) to the trash? You can restore them later.`}
        confirmLabel="Move to trash"
        loading={bulkTrashMutation.isPending}
        onConfirm={handleBulkTrash}
        onCancel={() => setBulkConfirm(false)}
      />
    </div>
  );
}

interface ReferralFormModalProps {
  partner: ReferralPartner | null;
  onClose: () => void;
  onSaved: () => void;
}

function ReferralFormModal({ partner, onClose, onSaved }: ReferralFormModalProps) {
  const isEdit = !!partner;
  const [type, setType] = useState<ReferralPartnerType>(partner?.type || 'OTHER');
  const [employeeId, setEmployeeId] = useState(partner?.employeeId || '');
  const [doctor, setDoctor] = useState<any>();
  const [employee, setEmployee] = useState<any>();
  const [name, setName] = useState(partner?.name || '');
  const [mobileNumber, setMobileNumber] = useState(partner?.mobileNumber || '');
  const [organization, setOrganization] = useState(partner?.organization || '');
  const [designation, setDesignation] = useState(partner?.designation || '');
  const [address, setAddress] = useState(partner?.address || '');
  const [defaultSharePercent, setDefaultSharePercent] = useState(Number(partner?.defaultSharePercent) || 0);
  const [isActive, setIsActive] = useState(partner?.isActive ?? true);
  const [notes, setNotes] = useState(partner?.notes || '');
  const [error, setError] = useState<string | null>(null);

  const createMutation = useCreateReferralPartner();
  const updateMutation = useUpdateReferralPartner();
  const saving = createMutation.isPending || updateMutation.isPending;

  // Close on Escape for a polished, keyboard-friendly experience.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const inputClass =
    'w-full px-3 py-2 rounded-lg border border-border bg-background text-text placeholder:text-text-muted/60 focus:outline-none focus:ring-2 focus:ring-primary text-sm transition-colors';
  const labelClass = 'block text-xs font-medium text-text-muted mb-1';

  const handleSave = async () => {
    setError(null);
    if (type === 'DOCTOR' || type === 'EMPLOYEE') {
      if (!employeeId) {
        setError(type === 'DOCTOR' ? 'Please select a doctor.' : 'Please select an employee.');
        return;
      }
    } else {
      if (!name.trim()) {
        setError('Name is required.');
        return;
      }
      if (!mobileNumber.trim()) {
        setError('Mobile number is required.');
        return;
      }
      if (!/^\+?[\d\s()-]{6,20}$/.test(mobileNumber.trim())) {
        setError('Enter a valid mobile number (digits, spaces, dashes or parentheses).');
        return;
      }
    }
    if (!(Number(defaultSharePercent) > 0)) {
      setError('Default referral % is required and must be greater than 0.');
      return;
    }

    const payload: Partial<ReferralPartner> = {
      type,
      isActive,
      defaultSharePercent: Number(defaultSharePercent) || 0,
      notes: notes.trim() || undefined,
      ...(type === 'DOCTOR' || type === 'EMPLOYEE'
        ? {
            employeeId,
            name:
              name.trim() ||
              (type === 'DOCTOR' ? doctor?.name : employee?.name) ||
              (type === 'DOCTOR' ? 'Doctor' : 'Employee'),
          }
        : {
            employeeId: undefined,
            name: name.trim(),
            mobileNumber: mobileNumber.trim(),
            organization: organization.trim() || undefined,
            designation: designation.trim() || undefined,
            address: address.trim() || undefined,
          }),
    };

    try {
      if (isEdit && partner) {
        await updateMutation.mutateAsync({ id: partner.id, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onSaved();
    } catch (err: any) {
      setError(err?.message || 'Failed to save referral partner.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 modal-overlay-enter">
      <div className="bg-surface rounded-2xl border border-border shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col modal-panel-enter">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border bg-background/50 rounded-t-2xl shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Handshake size={18} className="text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-text truncate">
                {isEdit ? 'Edit Referral Partner' : 'Add Referral Partner'}
              </h2>
              <p className="text-xs text-text-muted truncate">
                {isEdit ? 'Update this referrer’s details' : 'Register a doctor, employee or other referrer'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 hover:bg-background rounded-lg transition-colors shrink-0"
          >
            <X size={18} className="text-text-muted" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {error && (
            <div className="flex items-start gap-2 p-2.5 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
              <AlertCircle size={15} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Referral type */}
          <div>
            <SectionHeader icon={Users} title="Referral type" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              <TypeCard
                active={type === 'DOCTOR'}
                icon={Stethoscope}
                title="Doctor"
                description="A hospital or outside doctor"
                onClick={() => setType('DOCTOR')}
              />
              <TypeCard
                active={type === 'EMPLOYEE'}
                icon={UserRound}
                title="Employee"
                description="In-house staff member"
                onClick={() => setType('EMPLOYEE')}
              />
              <TypeCard
                active={type === 'OTHER'}
                icon={Building2}
                title="Other"
                description="Clinic, agent or any other referrer"
                onClick={() => setType('OTHER')}
              />
            </div>
          </div>

          {/* Referrer details */}
          <div>
            <SectionHeader
              icon={type === 'DOCTOR' ? Stethoscope : type === 'EMPLOYEE' ? UserRound : Building2}
              title="Referrer details"
            />
            {type === 'DOCTOR' ? (
              <div>
                <label className={labelClass}>Doctor *</label>
                <DoctorSearchSelect
                  value={employeeId}
                  onChange={(id, doc) => {
                    setEmployeeId(id);
                    setDoctor(doc);
                    if (doc) setName(doc.name || `${doc.firstName || ''} ${doc.lastName || ''}`.trim());
                  }}
                  showPreview={false}
                />
              </div>
            ) : type === 'EMPLOYEE' ? (
              <div>
                <label className={labelClass}>Employee *</label>
                <EmployeeSearchSelect
                  value={employeeId}
                  onChange={(id, emp) => {
                    setEmployeeId(id);
                    setEmployee(emp);
                    if (emp) setName(emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim());
                  }}
                  showPreview={false}
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className={labelClass} htmlFor="referral-name">
                    Name *
                  </label>
                  <input
                    id="referral-name"
                    type="text"
                    autoFocus
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Dr. Md. Rafiqul Islam"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="referral-mobile">
                    Mobile *
                  </label>
                  <input
                    id="referral-mobile"
                    type="tel"
                    inputMode="tel"
                    value={mobileNumber}
                    onChange={(e) => setMobileNumber(e.target.value)}
                    placeholder="+8801…"
                    className={inputClass}
                  />
                </div>
                <div>
                  <DesignationSearchSelect
                    id="referral-designation"
                    label="Designation"
                    value={designation}
                    onChange={setDesignation}
                    placeholder="Search designation…"
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="referral-organization">
                    Organization
                  </label>
                  <input
                    id="referral-organization"
                    type="text"
                    value={organization}
                    onChange={(e) => setOrganization(e.target.value)}
                    placeholder="Clinic / hospital / company"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="referral-address">
                    Address
                  </label>
                  <input
                    id="referral-address"
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Optional — street, area, city"
                    className={inputClass}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Compensation */}
          <div>
            <SectionHeader icon={Percent} title="Referral compensation" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
              <div>
                <label className={labelClass} htmlFor="referral-default-share">
                  Default referral % (legacy)
                </label>
                <div className="relative">
                  <ValidatedNumberInput
                    id="referral-default-share"
                    value={defaultSharePercent}
                    onChange={setDefaultSharePercent}
                    max={100}
                    placeholder="0.00"
                    className={`${inputClass} pr-8`}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-text-muted pointer-events-none">
                    %
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-text-muted">
                  Legacy default. OPD receipts now use the hospital base discount from Settings → General.
                </p>
              </div>
              <div>
                <label className={labelClass}>
                  Status {isActive ? '(Available for billing)' : '(Hidden from new referrals)'}
                </label>
                <ToggleSwitch
                  checked={isActive}
                  onChange={setIsActive}
                  label={isActive ? 'Active' : 'Inactive'}
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <SectionHeader icon={StickyNote} title="Notes" />
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Optional remarks about this referrer…"
              className={`${inputClass} resize-y`}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-3 shrink-0 bg-background/50 rounded-b-2xl">
          <p className="text-xs text-text-muted hidden sm:block">Fields marked * are required.</p>
          <div className="flex items-center gap-2 ml-auto">
            <button
              type="button"
              onClick={onClose}
              className="px-3.5 py-2 text-text-muted hover:text-text transition-colors text-sm flex items-center gap-2"
            >
              <X size={16} />
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : isEdit ? <Save size={16} /> : <Plus size={16} />}
              {isEdit ? 'Save Changes' : 'Create Referral'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
